import { levelById } from './levels';
import type { Source } from './types';
import { World, type Action } from './world';

export interface RunFile {
  format: string;
  level: { id: string; name: string; seed: number };
  ticks: number;
  actions: { t: number; src: Source; a: Action }[];
  report?: { metrics?: { goodReleases?: number; trustEnd?: number; outcome?: string } };
}

export function parseRunFile(text: string): RunFile {
  const data = JSON.parse(text) as RunFile;
  if (!data || typeof data !== 'object' || !String(data.format ?? '').startsWith('blast-radius-run/')) throw new Error('That isn’t a Blast Radius run log.');
  if (!levelById(data.level?.id)) throw new Error(`Unknown level "${data.level?.id}".`);
  if (!Array.isArray(data.actions)) throw new Error('The run log has no actions.');
  return data;
}

/**
 * Re-simulates a recorded run. Actions are applied at the exact tick they
 * were recorded, before that tick's step, which is how the live game applies
 * input between steps. Same seed + same actions = same world.
 */
export class Replayer {
  readonly world: World;
  readonly run: RunFile;
  private i = 0;

  constructor(run: RunFile) {
    this.run = run;
    const level = levelById(run.level.id)!;
    this.world = new World({ ...level, seed: run.level.seed });
  }

  get done(): boolean {
    return this.world.tick >= this.run.ticks || this.world.outcome !== 'playing';
  }

  /** Advance one tick, applying any actions recorded for it. */
  step() {
    const w = this.world;
    const acts = this.run.actions;
    while (this.i < acts.length && acts[this.i].t <= w.tick) {
      const { a, src } = acts[this.i++];
      w.apply(a, src);
    }
    if (!this.done) w.step();
  }

  /** Does the replayed outcome match what the run file recorded? */
  verify(): { ok: boolean; detail: string } {
    const m = this.run.report?.metrics;
    if (!m) return { ok: true, detail: 'No recorded totals to compare.' };
    const w = this.world;
    const sameReleases = m.goodReleases === undefined || m.goodReleases === w.goodReleases;
    const sameTrust = m.trustEnd === undefined || m.trustEnd === Math.round(w.trust);
    const sameOutcome = m.outcome === undefined || m.outcome === w.outcome;
    const ok = sameReleases && sameTrust && sameOutcome;
    return {
      ok,
      detail: ok
        ? `Replay matches the recording: ${w.goodReleases} good releases, trust ${Math.round(w.trust)}.`
        : `Replay differs from the recording (releases ${w.goodReleases} vs ${m.goodReleases}, trust ${Math.round(w.trust)} vs ${m.trustEnd}). The log may have been edited, or it came from a different version of the game.`,
    };
  }
}
